/*
  Predictive search idle state (T18/T19/T21, 2026-09-15): Recently
  searched / Popular searches / Recently viewed, shown when the search
  box is focused but empty. Popular searches is pure Liquid (a merchant
  link_list, see snippets/header-search.liquid) -- this file only
  handles the two localStorage-backed sections: recording what to show,
  and rendering it into the idle panel each time it opens.

  Recently searched: ported from a sibling-repo pattern (recording on
  the search-results page load, reading back into pills) -- rewritten
  here using real DOM construction (textContent/createElement) instead
  of that pattern's innerHTML template-literal approach, since search
  terms are user input and unescaped innerHTML with user input is a
  real XSS risk, not a style choice.

  Recently viewed: same idea (client-only, no live re-fetch -- data can
  go stale between view and later display, an accepted tradeoff), but
  rebuilt from scratch rather than porting the sibling-repo version,
  which was jQuery/Slick-dependent (this section is a plain top-4 row
  per Alex, no carousel needed) and had the same innerHTML/XSS issue
  plus a real bug (revisiting a product didn't move it to the front,
  just silently no-op'd). Snapshot data comes from a JSON script tag
  main-product.liquid renders per page view, not a full product fetch.
*/
(function () {
  var RECENT_SEARCHES_KEY = 'predictive_search_recent_searches';
  var RECENTLY_VIEWED_KEY = 'predictive_search_recently_viewed';
  var MAX_RECENT_SEARCHES = 5;
  var MAX_RECENTLY_VIEWED = 4;

  function readList(key) {
    try {
      var raw = localStorage.getItem(key);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeList(key, list) {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (error) {
      // localStorage unavailable (private mode, quota, disabled) -- the
      // feature just won't persist this visit, not worth surfacing.
    }
  }

  // ---------------------------------------------------------------------
  // Recording
  // ---------------------------------------------------------------------

  function recordSearchTerm() {
    var template = document.querySelector('main[data-template]')?.dataset.template;
    if (template !== 'search') return;

    var term = new URLSearchParams(window.location.search).get('q');
    term = term ? term.trim() : '';
    if (!term) return;

    var history = readList(RECENT_SEARCHES_KEY).filter(function (existingTerm) {
      return existingTerm.toLowerCase() !== term.toLowerCase();
    });
    history.unshift(term);
    writeList(RECENT_SEARCHES_KEY, history.slice(0, MAX_RECENT_SEARCHES));
  }

  function recordRecentlyViewed() {
    var snapshotTag = document.querySelector('[data-recently-viewed-snapshot]');
    if (!snapshotTag) return;

    var product;
    try {
      product = JSON.parse(snapshotTag.textContent);
    } catch (error) {
      return;
    }
    if (!product || !product.id) return;

    var history = readList(RECENTLY_VIEWED_KEY).filter(function (existingProduct) {
      return existingProduct.id !== product.id;
    });
    history.unshift(product);
    writeList(RECENTLY_VIEWED_KEY, history.slice(0, MAX_RECENTLY_VIEWED));
  }

  recordSearchTerm();
  recordRecentlyViewed();

  // ---------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------

  function renderRecentSearches(root) {
    var section = root.querySelector('[data-recent-searches-section]');
    var list = root.querySelector('[data-recent-searches-list]');
    if (!section || !list) return;

    var history = readList(RECENT_SEARCHES_KEY);
    list.innerHTML = '';

    if (!history.length) {
      section.hidden = true;
      return;
    }

    section.hidden = false;

    history.forEach(function (term) {
      var li = document.createElement('li');
      li.className = 'predictive-search__pill';

      var link = document.createElement('a');
      link.className = 'predictive-search__pill-text';
      link.href = routes.search_url + '?q=' + encodeURIComponent(term);
      link.textContent = term;
      li.appendChild(link);

      var removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'predictive-search-idle__pill-remove';
      removeButton.dataset.removeSearchTerm = term;
      removeButton.setAttribute('aria-label', window.predictiveSearchIdleStrings.removeSearchTerm.replace('[term]', term));
      removeButton.textContent = '×';
      li.appendChild(removeButton);

      list.appendChild(li);
    });
  }

  function renderRecentlyViewed(root) {
    var section = root.querySelector('[data-recently-viewed-section]');
    var list = root.querySelector('[data-recently-viewed-list]');
    if (!section || !list) return;

    var history = readList(RECENTLY_VIEWED_KEY);
    list.innerHTML = '';

    if (!history.length) {
      section.hidden = true;
      return;
    }

    section.hidden = false;

    var iconTemplate = document.querySelector('[data-recently-viewed-quick-add-icon]');

    history.forEach(function (product) {
      var li = document.createElement('li');

      // The quick-add button must not live inside the <a> (interactive content
      // in a link is invalid), so the card is a wrapper holding the link and,
      // as its SIBLING, an anchor box that positions the button over the image.
      var wrap = document.createElement('div');
      wrap.className = 'predictive-search__card-wrap';

      var link = document.createElement('a');
      link.className = 'predictive-search__card';
      link.href = product.url;

      var quickAddAnchor = null;

      if (product.image) {
        var media = document.createElement('div');
        media.className = 'predictive-search__card-media';

        var img = document.createElement('img');
        img.className = 'predictive-search__card-image';
        img.src = product.image;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);

        // singleVariant: quick add only for products without options (snapshots saved before this field existed have none, so no button)
        if (product.variantId && product.available && product.singleVariant === true) {
          quickAddAnchor = document.createElement('div');
          quickAddAnchor.className = 'predictive-search__card-quick-add-anchor';

          var quickAdd = document.createElement('button');
          quickAdd.type = 'button';
          quickAdd.className = 'predictive-search__card-quick-add';
          quickAdd.setAttribute('aria-label', window.variantStrings.addToCart + ' ' + product.title);
          quickAdd.dataset.quickAddVariantId = product.variantId;
          if (iconTemplate) {
            quickAdd.appendChild(iconTemplate.content.cloneNode(true));
          }
          quickAddAnchor.appendChild(quickAdd);
        }

        link.appendChild(media);
      }

      var title = document.createElement('p');
      title.className = 'predictive-search__card-title';
      title.textContent = product.title;
      link.appendChild(title);

      var price = document.createElement('p');
      price.className = 'predictive-search__card-price';
      price.textContent = product.price;
      link.appendChild(price);

      wrap.appendChild(link);
      if (quickAddAnchor) wrap.appendChild(quickAddAnchor);
      li.appendChild(wrap);
      list.appendChild(li);
    });
  }

  // ---------------------------------------------------------------------
  // Quick add (recently-viewed card)
  // ---------------------------------------------------------------------

  function quickAddToCart(button) {
    var variantId = button.dataset.quickAddVariantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('predictive-search__card-quick-add--loading');

    var cart = document.querySelector('cart-drawer');
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
    }

    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          // Add failed (sold out mid-session, etc.) -- nothing bespoke to show
          // here; the product page itself is one click away via the card link.
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        }
      })
      .finally(function () {
        button.disabled = false;
        button.classList.remove('predictive-search__card-quick-add--loading');
        // Real bug, found live: renderContents() only clears "is-empty" from
        // the .drawer__inner descendant, not from <cart-drawer> itself -- the
        // element product-form.js's own add-to-cart flow actually clears it
        // on (its finally() block does this exact line). Skipping it left
        // <cart-drawer> carrying is-empty after the first add from a
        // genuinely empty cart, and CSS scoped to cart-drawer.is-empty was
        // hiding the real cart form/items entirely (correct total/count,
        // but a blank drawer body).
        if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      });
  }

  function renderIdleState(root) {
    renderRecentSearches(root);
    renderRecentlyViewed(root);
  }

  document.addEventListener('predictive-search:idle', function (event) {
    var root = event.target.querySelector('[data-predictive-search-idle]');
    if (root) renderIdleState(root);
  });

  // ---------------------------------------------------------------------
  // Clear all / per-item remove
  // ---------------------------------------------------------------------

  document.addEventListener('click', function (event) {
    // Quick add is shared between the idle state's Recently Viewed cards
    // and real search results' Products cards (sections/predictive-search.liquid)
    // -- scoped to .predictive-search generally, not just the idle root.
    var quickAddButton = event.target.closest('.predictive-search__card-quick-add');
    if (quickAddButton && quickAddButton.closest('.predictive-search')) {
      event.preventDefault();
      event.stopPropagation();
      quickAddToCart(quickAddButton);
      return;
    }

    var idleRoot = event.target.closest('[data-predictive-search-idle]');
    if (!idleRoot) return;

    if (event.target.closest('[data-clear-recent-searches]')) {
      writeList(RECENT_SEARCHES_KEY, []);
      renderRecentSearches(idleRoot);
      return;
    }

    if (event.target.closest('[data-clear-recently-viewed]')) {
      writeList(RECENTLY_VIEWED_KEY, []);
      renderRecentlyViewed(idleRoot);
      return;
    }

    var removeButton = event.target.closest('[data-remove-search-term]');
    if (removeButton) {
      var term = removeButton.dataset.removeSearchTerm;
      var remaining = readList(RECENT_SEARCHES_KEY).filter(function (existingTerm) {
        return existingTerm !== term;
      });
      writeList(RECENT_SEARCHES_KEY, remaining);
      renderRecentSearches(idleRoot);
    }
  });
})();
